import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { usePayerStatusHistory } from '@/hooks/usePayerStatusHistory';
import { normalizePayerStatus, NORMALIZED_STATUS_LABELS } from '@/lib/normalization/statusNormalization';

interface AddPayerStatusProps {
  authorizationId: string;
  onStatusAdded?: () => void;
}

type StatusSource = 'portal' | 'fhir' | 'fax' | 'phone' | 'manual';

const SOURCE_OPTIONS: { value: StatusSource; label: string }[] = [
  { value: 'portal', label: 'Payer Portal' },
  { value: 'fhir', label: 'FHIR/ePAS' },
  { value: 'fax', label: 'Fax Response' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'manual', label: 'Manual Entry' },
];

export function AddPayerStatus({ authorizationId, onStatusAdded }: AddPayerStatusProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [rawStatus, setRawStatus] = useState('');
  const [source, setSource] = useState<StatusSource>('portal');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { addStatusEntry } = usePayerStatusHistory({ authorizationId });

  const normalizedPreview = normalizePayerStatus(rawStatus);

  const handleSubmit = async () => {
    if (!rawStatus.trim()) {
      toast.error('Please enter a status');
      return;
    }

    setIsSubmitting(true);
    try {
      await addStatusEntry(rawStatus, source, notes || undefined);
      toast.success('Status recorded');
      setIsOpen(false);
      setRawStatus('');
      setNotes('');
      onStatusAdded?.();
    } catch (err) {
      toast.error('Failed to record status');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1">
          <Plus className="h-3 w-3" />
          Add Status
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record Payer Status</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Source</Label>
            <Select value={source} onValueChange={(v) => setSource(v as StatusSource)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Raw Status (as shown by payer)</Label>
            <Input
              value={rawStatus}
              onChange={(e) => setRawStatus(e.target.value)}
              placeholder="e.g., Pending Clinical Review, Awaiting Decision..."
            />
            {rawStatus && normalizedPreview && (
              <p className="text-xs text-muted-foreground">
                Will be normalized to: <span className="font-medium">{NORMALIZED_STATUS_LABELS[normalizedPreview]}</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context..."
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || !rawStatus}>
              Record Status
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
